<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { default as PDialog } from 'primevue/dialog'
import { default as PButton } from 'primevue/button'
import { default as PDataTable } from 'primevue/datatable'
import { default as PColumn } from 'primevue/column'
import { default as PProgressSpinner } from 'primevue/progressspinner'
import { default as PMessage } from 'primevue/message'
import { default as PInputText } from 'primevue/inputtext'
import { default as PPaginator } from 'primevue/paginator'
import { default as PTag } from 'primevue/tag'
import type { AvailableEducationalMember } from '@/core/domain/school/user/AvailableEducationalMember'
import type School from '@/core/domain/school/core/School'
import type { PaginatedResponse } from '@/core/domain/resources/types/Pagination'
import { useToast } from 'primevue/usetoast'
import DependencyContainer from '@/presentation/configuration/DependencyContainer'
import FetchAvailableEducationalMembersUseCase from '@/core/useCases/school/user/FetchAvailableEducationalMembersUseCase'
import { useSchoolPeriodStore } from '@/presentation/stores/school/schoolPeriod'
import { useSchoolPeriodUserStore } from '@/presentation/stores/school/schoolPeriodUser'
import { UserRole } from '@/core/domain/resources/enums/UserRole'
import { storeToRefs } from 'pinia'

// Props
const props = defineProps<{
  visible: boolean
  school: School
  defaultRole?: UserRole.SchoolValues
}>()

// Emits
const emit = defineEmits<{
  'update:visible': [value: boolean]
  'members-added': [members: AvailableEducationalMember[]]
}>()

// Composables
const toast = useToast()
const schoolPeriodStore = useSchoolPeriodStore()
const schoolPeriodUserStore = useSchoolPeriodUserStore()
const { selectedSchoolPeriod } = storeToRefs(schoolPeriodStore)
const { schoolPeriodUsers } = storeToRefs(schoolPeriodUserStore)

// State
const loading = ref(false)
const error = ref('')
const paginatedResponse = ref<PaginatedResponse<AvailableEducationalMember> | null>(null)
const selectedMembers = ref<AvailableEducationalMember[]>([])
const searchQuery = ref('')

// Pagination state
const currentPage = ref(0)
const pageSize = ref(10)
const sortField = ref<string | null>(null)
const sortOrder = ref<number>(1)

// Computed
const dialogVisible = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value)
})

const hasSelectedMembers = computed(() => selectedMembers.value.length > 0)

const availableMembers = computed(() => {
  if (!paginatedResponse.value || !paginatedResponse.value.content) {
    return []
  }
  // S'assurer que content est bien un tableau
  if (!Array.isArray(paginatedResponse.value.content)) {
    console.error('paginatedResponse.content is not an array:', paginatedResponse.value.content)
    return []
  }

  // Filtrer les membres qui ne sont pas déjà dans la période scolaire
  return paginatedResponse.value.content
})

// Nombre total de membres disponibles après filtrage des membres existants
const filteredAvailableMembersCount = computed(() => availableMembers.value.length)

const totalRecords = computed(() => paginatedResponse.value?.totalElements || 0)
const hasNextPage = computed(() => paginatedResponse.value?.hasNext || false)
const hasPreviousPage = computed(() => paginatedResponse.value?.hasPrevious || false)

const filteredMembers = computed(() => {
  if (!searchQuery.value) return availableMembers.value

  const query = searchQuery.value.toLowerCase()
  return availableMembers.value.filter((member) => {
    return member.matchesSearch(query)
  })
})

// Methods
/**
 * Obtient la classe CSS à utiliser pour un rôle
 * @param role Rôle de l'utilisateur
 * @returns Classe CSS correspondante
 */
function getRoleSeverity(role: UserRole.Values): string {
  switch (role) {
    case UserRole.Values.TEACHER:
      return 'info'
    case UserRole.Values.STUDENT:
      return 'success'
    case UserRole.Values.CPE:
      return 'warning'
    case UserRole.Values.PERDIR:
      return 'danger'
    default:
      return 'secondary'
  }
}
async function loadAvailableMembers() {
  if (!selectedSchoolPeriod.value || !props.school?.id) {
    error.value = 'Période scolaire ou établissement non sélectionné'
    return
  }

  try {
    loading.value = true
    error.value = ''

    const fetchAvailableEducationalMembersUseCase = DependencyContainer.resolve(
      FetchAvailableEducationalMembersUseCase
    )

    const sortBy = sortField.value
      ? [`${sortField.value}:${sortOrder.value === 1 ? 'asc' : 'desc'}`]
      : undefined

    const users = await fetchAvailableEducationalMembersUseCase.execute(
      props.school.id,
      selectedSchoolPeriod.value.schoolPeriodId,
      {
        page: currentPage.value,
        limit: pageSize.value,
        sortBy
      }
    )

    // S'assurer que users est bien un objet PaginatedResponse valide
    if (!users || !Array.isArray(users.content)) {
      throw new Error('Invalid response format from fetchAvailableEducationalMembers')
    }

    paginatedResponse.value = users
  } catch (err) {
    error.value =
      err instanceof Error ? err.message : 'Erreur lors du chargement des membres disponibles'

    toast.add({
      severity: 'error',
      summary: 'Erreur',
      detail: error.value,
      life: 5000
    })
  } finally {
    loading.value = false
  }
}

async function addSelectedMembers() {
  if (!hasSelectedMembers.value) return
  if (!selectedSchoolPeriod.value || !props.school?.id) {
    error.value = 'Période scolaire ou établissement non sélectionné'
    return
  }

  try {
    loading.value = true
    error.value = ''

    // Utiliser le store pour ajouter les membres existants avec le rôle par défaut
    const defaultRole = props.defaultRole || UserRole.SchoolValues.TEACHER
    const result = await schoolPeriodUserStore.addExistingMembersToSchoolPeriod(
      props.school.id,
      selectedSchoolPeriod.value.schoolPeriodId,
      selectedMembers.value,
      defaultRole
    )

    // Afficher un message de succès avec détails
    if (result.successful.length > 0) {
      toast.add({
        severity: 'success',
        summary: 'Succès',
        detail: `${result.successful.length} membre(s) ajouté(s) avec succès`,
        life: 3000
      })
    }

    // Afficher les erreurs s'il y en a
    if (result.failed.length > 0) {
      const failedNames = result.failed
        .map((f) => `${f.member.firstname} ${f.member.lastname}`)
        .join(', ')
      toast.add({
        severity: 'warn',
        summary: 'Attention',
        detail: `${result.failed.length} membre(s) n'ont pas pu être ajoutés: ${failedNames}`,
        life: 5000
      })
    }

    // Émettre l'événement avec les membres ajoutés avec succès
    if (result.successful.length > 0) {
      emit(
        'members-added',
        result.successful.map((u) => {
          // Convertir SchoolPeriodUser en AvailableEducationalMember pour l'événement
          return {
            identifier: u.userIdentifier,
            firstname: u.firstname,
            lastname: u.lastname,
            username: u.username || '',
            email: u.email || ''
          } as AvailableEducationalMember
        })
      )
    }

    // Réinitialiser et fermer si au moins un membre a été ajouté
    if (result.successful.length > 0) {
      selectedMembers.value = []
      dialogVisible.value = false
    }
  } catch (err) {
    error.value = err instanceof Error ? err.message : "Erreur lors de l'ajout des membres"

    toast.add({
      severity: 'error',
      summary: 'Erreur',
      detail: error.value,
      life: 5000
    })
  } finally {
    loading.value = false
  }
}

function onPageChange(event: any) {
  currentPage.value = event.page
  pageSize.value = event.rows
  loadAvailableMembers()
}

function onSort(event: any) {
  sortField.value = event.sortField
  sortOrder.value = event.sortOrder
  currentPage.value = 0 // Reset to first page on sort
  loadAvailableMembers()
}

function onHide() {
  // Réinitialiser l'état lors de la fermeture
  selectedMembers.value = []
  searchQuery.value = ''
  currentPage.value = 0
  error.value = ''
}

// Lifecycle
onMounted(() => {
  if (props.visible) {
    loadAvailableMembers()
  }
})

// Watch for dialog opening
watch(
  () => props.visible,
  (newValue) => {
    if (newValue) {
      loadAvailableMembers()
    }
  }
)
</script>

<template>
  <p-dialog
    v-model:visible="dialogVisible"
    modal
    header="Ajouter des membres existants"
    :style="{ width: '900px' }"
    :closable="!loading"
    @hide="onHide"
  >
    <!-- Message d'erreur -->
    <p-message v-if="error" severity="error" :text="error" class="mb-4 w-full" />

    <!-- Barre de recherche -->
    <div class="mb-4">
      <span class="p-input-icon-left w-full">
        <i class="pi pi-search" />
        <p-input-text
          v-model="searchQuery"
          placeholder="Rechercher par nom ou email..."
          class="w-full"
          :disabled="loading"
        />
      </span>
    </div>

    <!-- Table des membres disponibles -->
    <div v-if="loading" class="flex items-center justify-center p-8">
      <p-progress-spinner style="width: 50px; height: 50px" />
    </div>

    <div v-else>
      <p-data-table
        v-model:selection="selectedMembers"
        :value="filteredMembers"
        dataKey="identifier"
        :paginator="false"
        :rows="pageSize"
        :loading="loading"
        :scrollable="true"
        scrollHeight="400px"
        @sort="onSort"
      >
        <p-column selectionMode="multiple" :style="{ width: '3rem' }" />

        <p-column field="lastname" header="Nom" sortable>
          <template #body="{ data }">
            {{ data.lastname }}
          </template>
        </p-column>

        <p-column field="firstname" header="Prénom" sortable>
          <template #body="{ data }">
            {{ data.firstname }}
          </template>
        </p-column>

        <p-column field="email" header="Email" sortable>
          <template #body="{ data }">
            {{ data.email || '-' }}
          </template>
        </p-column>

        <p-column header="Rôles">
          <template #body="{ data }">
            <div v-if="data.roles && data.roles.length > 0" class="flex flex-wrap gap-1">
              <p-tag
                v-for="(role, idx) in data.roles"
                :key="idx"
                :value="UserRole.label(role)"
                :severity="getRoleSeverity(role)"
              />
            </div>
            <span v-else class="text-surface-500">-</span>
          </template>
        </p-column>

        <template #empty>
          <div class="p-4 text-center">
            <p v-if="filteredAvailableMembersCount === 0 && totalRecords > 0">
              Tous les membres disponibles sont déjà ajoutés à cette période scolaire.
            </p>
            <p v-else>
              Aucun membre disponible à ajouter.
            </p>
          </div>
        </template>
      </p-data-table>

      <!-- Pagination -->
      <p-paginator
        v-if="totalRecords > pageSize"
        :rows="pageSize"
        :totalRecords="totalRecords"
        :rowsPerPageOptions="[10, 20, 50]"
        :first="currentPage * pageSize"
        @page="onPageChange"
        class="mt-4"
      />
    </div>

    <!-- Footer avec actions -->
    <template #footer>
      <div class="flex items-center justify-between">
        <span v-if="hasSelectedMembers" class="text-surface-500 text-sm">
          {{ selectedMembers.length }} membre(s) sélectionné(s)
        </span>
        <span v-else></span>

        <div class="flex gap-2">
          <p-button
            label="Annuler"
            severity="secondary"
            @click="dialogVisible = false"
            :disabled="loading"
          />
          <p-button
            label="Ajouter les membres sélectionnés"
            severity="primary"
            @click="addSelectedMembers"
            :disabled="!hasSelectedMembers || loading"
            :loading="loading"
          />
        </div>
      </div>
    </template>
  </p-dialog>
</template>
